import { useState, useEffect, useCallback } from 'react';
import { Container, Form, ProgressBar } from 'react-bootstrap';
import Stepper from 'react-stepper-horizontal';

import { PageHeader } from '../components/Layout/PageHeader';
import { DataCollectionNotice } from '../components/Layout/DataCollectionNotice';
import { ApplicantInfo } from '../components/ApplicantInfo';
import { LPGInstallation } from '../components/ConstructionPermit/LPGInstallation';
import { InstallationAddress } from '../components/ConstructionPermit/InstallationAddress';
import { PlotPlan } from '../components/PlotPlan';
import { SubmitSuccess } from '../components/SubmitSuccess';
import { SomethingBroke } from '../components/Errors/SomethingBroke';
import FormClientError from '../components/Errors/FormClientError';
import { InternalServerError } from '../components/Errors/InternalServerError';
import dayjs from 'dayjs';

export function ConstructionPermit() {
	const [step, setStep] = useState(1);

	const [somethingBroke, setSomethingBroke] = useState(false);

	const [currApplicant, setCurrApplicant] = useState({
		name: '',
		phone: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		email: '',
	});

	const [currInstallationAddr, setCurrInstallationAddr] = useState({
		street: '',
		city: '',
		state: 'UT',
		zip: '',
	});

	const [currInstallationDetails, setCurrInstallationDetails] = useState({
		type: 'Above Ground',
		size: '',
		contractor: '',
		permit: '',
		parcelNumbers: [{ parcelNumber: '' }],
	});

	const [currPlotPlan, setCurrPlotPlan] = useState({
		file: {},
		answer: false,
	});

	const [httpStatusCode, setHttpStatusCode] = useState(null);

	const [submitDone, setSubmitDone] = useState(false);

	const incrementStep = useCallback(() => {
		setStep(curStep => curStep + 1);
	}, []);

	const decrementStep = useCallback(() => {
		setStep(curStep => curStep - 1);
	}, []);

	const createBody = plotPlan => {
		const data = {
			name: currApplicant.name,
			phone: currApplicant.phone,
			city: currApplicant.city,
			email: currApplicant.email,
			state: currApplicant.state,
			mailingAddress: currApplicant.address,
			zip: currApplicant.zip,
			installationAddress: currInstallationAddr.street,
			installationCity: currInstallationAddr.city,
			installationZip: currInstallationAddr.zip,
			installationState: currInstallationAddr.state,
			class: currInstallationDetails.class,
			tankSize: currInstallationDetails.size,
			licensedContractor: currInstallationDetails.contractor,
			parcelNumber: currInstallationDetails.parcelNumber,
			ucBuildingPermit: currInstallationDetails.permit,
			type: currInstallationDetails.type,
			date: dayjs().format('MM/DD/YYYY'),
			plotPlan: plotPlan.answer,
		};

		const body = new FormData();
		body.append('file', plotPlan.file);

		for (var key in data) {
			body.append(key, data[key]);
		}

		return body;
	};

	const submitForm = async plotPlan => {
		try {
			const { status } = await fetch(
				`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}/lpg-construction`,
				{
					method: 'POST',
					body: createBody(plotPlan),
				}
			);

			setHttpStatusCode(status);
			setSubmitDone(false);
		} catch (err) {
			console.error(err);
			setSomethingBroke(true);
		}
	};

	const handleOnNext = (setData = () => {}) => {
		return data => {
			setData(data);
			incrementStep();
		};
	};

	const handleSubmit = (setData = () => {}) => {
		return async data => {
			setData(data);
			setSubmitDone(true);
			submitForm(data);
		};
	};

	useEffect(() => {
		if (httpStatusCode === 200) {
			incrementStep();
		}
	}, [httpStatusCode, incrementStep]);

	if (somethingBroke === true) {
		return <SomethingBroke></SomethingBroke>;
	}

	if (httpStatusCode >= 500) {
		return <InternalServerError></InternalServerError>;
	}

	if (httpStatusCode >= 400) {
		return (
			<FormClientError
				returnToForm={() => {
					setStep(1);
					setHttpStatusCode(null);
				}}
			/>
		);
	}

	const steps = [
		{
			component: (
				<ApplicantInfo
					currApplicant={currApplicant}
					onNext={handleOnNext(setCurrApplicant)}
					finalStep={false}
					disablePrev={true}
					disableNext={false}
					key="appInfo"
				/>
			),

			title: 'Applicant Information',
		},

		{
			component: (
				<InstallationAddress
					currInstallationAddr={currInstallationAddr}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrInstallationAddr)}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
					key="installAddr"
				/>
			),

			title: 'Installation Address',
		},

		{
			component: (
				<LPGInstallation
					currInstallationDetails={currInstallationDetails}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrInstallationDetails)}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
					key="LPGInst"
				/>
			),

			title: 'Installation Details',
		},

		{
			component: (
				<PlotPlan
					currPlotPlan={currPlotPlan}
					onPrevious={decrementStep}
					onSubmit={handleSubmit(setCurrPlotPlan)}
					finalStep={true}
					disableNext={false}
					disablePrev={false}
					enableLoading={submitDone}
					key="plotPlan"
				/>
			),

			title: 'Plot Plan',
		},
		{
			component: (
				<SubmitSuccess
					finalStep={true}
					disablePrev={true}
					disableNext={true}
					key="submitSuccess"
				/>
			),

			title: 'Submission',
		},
	];

	return (
		<Container fluid className="p-0 mb-5 permit">
			<PageHeader title="LPG Construction Permit" />

			<Form className="app-container">
				<DataCollectionNotice />

				<div className="stepper">
					<Stepper
						steps={steps.map(({ title }, i) => ({
							title,
							onClick: () => setStep(i + 1),
						}))}
						activeStep={step - 1}
					/>
				</div>

				<div className="bar">
					<ProgressBar className="mb-4" now={(step / steps.length) * 100} />
				</div>

				{steps.map(({ component }, index) =>
					step === index + 1 ? component : ''
				)}
			</Form>
		</Container>
	);
}
